import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import Utils from '@/common/utils/utils';
import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';
import { updatePanelInitData } from '@/component/multiPanel/panel/handler/PanelHandler';
import { getItemFieldListByFieldId } from '@/service/formManagementService';
import { makeStyles, Popover } from '@material-ui/core';
import { useEffect, useState } from 'react';
import './TelephoneCallStatusUI.css';
import { PropTypes } from 'prop-types';
import { tr, trLabel } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';

/**
 * Get CSS class name and inline style for telephone call status based on status name
 * @param {string} statusName - Status name in Japanese (e.g., '通話待ち')
 * @returns {object} Object with className
 */
const getTelephoneCallStatusStyle = (statusName) => {
    if (!statusName) {
        return { className: '' };
    }

    const statusStyleMap = {
        [TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.CALL_WAITING]: {
            className: 'telephone-call-waiting',
        },
        [TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.DURING_CALL]: {
            className: 'telephone-during-call',
        },
        [TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.END_CALL]: {
            className: 'telephone-end-call',
        },
        [TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.MISSING_CALL]: {
            className: 'telephone-missing-call',
        },
        [TelephoneConstant.TELEPHONE_CALL_STATUS_LABEL.CANCELLED_CALL]: {
            className: 'telephone-cancelled-call',
        }
    };

    return statusStyleMap[statusName] || { className: '' };
};

/**
 * Render list of telephone call status items in popover 
 * @param {Array} itemList - List of status items
 * @param {Function} setCurrentItemData - Function to set current selected item
 * @param {Function} _setCallStatusListPopover - Function to close popover
 * @param {Object} currentItemData - Current selected item data
 * @returns {JSX.Element} Rendered status item list
 */
const renderCallStatusItemList = (
    itemList, 
    setCurrentItemData, 
    _setCallStatusListPopover, 
    currentItemData
) => {
    let itemListConvert = [];

    const onClickHandler = itemId => {
        const currentItem = itemList.find(item => {
            return item.id === itemId;
        });
        if (!currentItem) return;
        
        const itemStructure = Utils.convertJSONDataToObject(currentItem.structure);
        setCurrentItemData({
            id: currentItem.id,
            name: Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, currentItem.name),
            sysfield: itemStructure.sysfield || currentItem.sysfield
        });
        _setCallStatusListPopover(null);
    };

    if (!Utils.isEmptyArray(itemList)) {
        itemList?.forEach(item => {
            let itemStructure = Utils.convertJSONDataToObject(item.structure);
            let itemData = {
                id: item.id,
                name: Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, item.name),
                sysfield: itemStructure.sysfield || item.sysfield
            };
            itemListConvert.push(itemData);
        });
    }

    return (
        <div className="telephone-call-status-item-list-content">
            {itemListConvert?.map(item => {
                const isActive = item?.id === currentItemData?.id;
                const className = isActive 
                    ? "telephone-call-status-item-wrap active" 
                    : "telephone-call-status-item-wrap";
                // Get style based on status name (not ID, because ID is tag ID)
                const statusStyle = getTelephoneCallStatusStyle(item?.name);
                
                return (
                    <div key={`call-status-item-${item?.id}`} className={className}>
                        <div
                            className={`telephone-call-status-item-content ${statusStyle.className}`}
                            key={`${item?.id}`}
                            value={item?.id}
                            onClick={e => {
                                onClickHandler(item?.id);
                            }}
                        >
                            {tr(item?.name)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

/**
 * TelephoneCallStatusFieldUI Component
 * Display telephone call status field with dropdown selection
 * @param {Object} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const TelephoneCallStatusFieldUI = props => {
    // Re-render when the translation dictionary loads/extends so status names update.
    useTranslationSubscription();

    const [currentItemData, setCurrentItemData] = useState({});
    const [callStatusItemList, setCallStatusItemList] = useState([]);

    const [callStatusListPopover, setCallStatusListPopover] = useState(null);
    const isCallStatusListPopover = Boolean(callStatusListPopover);
    const idCallStatusListPopover = isCallStatusListPopover ? 'change-call-status' : '';

    const panelStatusKey = props.panelStatusKey;
    /**
     * Check if ticket can be edited
     * @returns {boolean} True if ticket can be edited
     */
    const checkTicketEnableEdit = () => {
       return false;
    };

    // Initialize current item data from defaultValue or currentItemData
    useEffect(() => {
        try {
            // Skip if subscription just updated (subscriptionFieldValue has same id as new value)
            // This prevents init useEffect from overriding subscription update
            if (props?.subscriptionFieldValue?.id) return;
            
            const currentPanelInitData = props.currentPanelInitData;
            if (
                currentPanelInitData &&
                !Utils.isEmptyObject(currentPanelInitData)
            ) {
                const currentPanelStatusData = currentPanelInitData?.[panelStatusKey];
                if (currentPanelStatusData) {
                    setCurrentItemData(currentPanelStatusData);
                    return;
                }
            }
    
            // If currentItemData is provided (object with id, name, etc)
            if (props.currentItemData && !Utils.isEmptyObject(props.currentItemData)) {
                setCurrentItemData(props.currentItemData);
                return;
            }
    
            // If defaultValue is provided (string ID), find the item from list
            if (!Utils.isEmptyString(props.defaultValue)) {
                if (Utils.isEmptyArray(callStatusItemList)) {
                    return;
                }
    
                // Clean defaultValue - remove all quotes and parse if JSON string
                let cleanDefaultValue = props.defaultValue;
                try {
                    cleanDefaultValue = JSON.parse(props.defaultValue);
                } catch (e) {
                    cleanDefaultValue = props.defaultValue.replace(/"/g, '');
                }
                
                // Find item by id or sysField (defaultValue can be either)
                const selectedItem = callStatusItemList.find(
                    item => item.id === cleanDefaultValue || item.sysField === cleanDefaultValue
                );
                if (selectedItem) {
                    const itemStructure = Utils.convertJSONDataToObject(
                        selectedItem.structure
                    );
                    const itemData = {
                        id: selectedItem.id,
                        name: Utils.getTextByLanguage(
                            Constant.LANGUAGE_TYPE.JP, 
                            selectedItem.name
                        ),
                        sysfield: itemStructure.sysfield || selectedItem.sysField
                    };
                    setCurrentItemData(itemData);
                }
            }
        } catch (error) {
            console.error('Error initializing telephone call status:', error);
        }
    }, [
        props?.subscriptionFieldValue,
        props.currentItemData, 
        props.currentPanelInitData?.[panelStatusKey],
        panelStatusKey,
        props.defaultValue, 
        callStatusItemList
    ]);

    // Handle subscription updates for telephone status
    useEffect(() => {
        const subsTelephoneStatus = props?.subscriptionFieldValue;
        if (!subsTelephoneStatus) return;
        if (Utils.isEmptyObject(subsTelephoneStatus)) return;

        const updateStatusId = subsTelephoneStatus?.id;
        const currentStatusId = currentItemData?.id;
        if (updateStatusId && updateStatusId !== currentStatusId) {
            setCurrentItemData(subsTelephoneStatus);
        }

    }, [props?.subscriptionFieldValue]);

    // Load call status item list
    useEffect(() => {
        (async () => {
            try {
                const fieldId = props.fieldData?.id || props.data?.id;
                const affiliationOrganization = 
                    props.fieldData?.affiliationOrganization || 
                    props.data?.affiliationOrganization;
                
                if (!fieldId || !affiliationOrganization) {
                    return;
                }

                const getListCallStatus = await getItemFieldListByFieldId(
                    props?.API,
                    Constant.FORM_USAGE.STATUS,
                    affiliationOrganization,
                    fieldId
                );
                
                if (
                    getListCallStatus &&
                    Array.isArray(getListCallStatus) &&
                    getListCallStatus.length > 0
                ) {
                    setCallStatusItemList(getListCallStatus);
                }
            } catch (error) {
                console.error('Error loading call status list:', error);
            }
        })();
    }, [props.fieldData, props.data, props.isHelpDesk, props.API]);

    // Update panel init data when current item changes
    useEffect(() => {
        if (
            currentItemData?.id && 
            currentItemData?.id !== props?.currentItemData?.id && 
            !Utils.isEmpty(props?.currentItemData)
        ) {
            if (props.setIsEdited) {
                props.setIsEdited(true);
            }
            if (props.setIsEditTicket) {
                props.setIsEditTicket(true);
            }
            if (typeof props?.setShowButtonEditTicket === 'function') {
                props.setShowButtonEditTicket();
            }
            if (!props?.listFieldEdited?.includes(props?.name)) {
                props?.setListFieldEdited([...props.listFieldEdited, props?.name]);
            }
            if (props?.setOnChangeEdit) {
                props?.setOnChangeEdit(!props.onChangeEdit);
            }
        }

        updatePanelInitData(
            props.panelId, 
            props.tabId, 
            props.panelInitData, 
            props.setPanelInitData, 
            panelStatusKey,
            currentItemData
        );
    }, [currentItemData, panelStatusKey]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setCurrentItemData({});
            setCallStatusItemList([]);
            setCallStatusListPopover(null);
        };
    }, []);

    // Reset when flag reset is triggered
    useEffect(() => {
        if (props?.flagReset) {
            setCurrentItemData(props.currentItemData);
            if (props.setFlagReset) {
                props.setFlagReset(false);
            }
        }
    }, [props?.flagReset, props.currentItemData, props.setFlagReset]);

    const cursorStyle = !checkTicketEnableEdit() 
        ? 'context-menu !important' 
        : 'pointer';

    // Get status style based on currentItemData.name (not ID, because ID is tag ID)
    const statusStyle = getTelephoneCallStatusStyle(currentItemData?.name);
    
    const useStyles = makeStyles(theme => ({
        callStatusField: {
            cursor: cursorStyle
        }
    }));

    const classes = useStyles();

    const onClickHandler = event => {
        if (checkTicketEnableEdit()) {
            setCallStatusListPopover(event.currentTarget);
        }
    };

    return (
        <div 
            key={props.key} 
            className="form-addTicket form-addTicket-field-telephone-call-status"
        >
            <label className="form-addTicket-label">
                {trLabel(TelephoneConstant.TELEPHONE_FIELD_LABEL.CALL_STATUS)}{' '}
            </label>
            <>
                {currentItemData?.name && (
                    <OverFlowTooltipWithTabsMui
                        value={tr(currentItemData?.name)}
                        onClick={event => {
                            onClickHandler(event);
                        }}
                        id={idCallStatusListPopover}
                        className={`${classes.callStatusField} telephone-call-status-ui ${statusStyle.className}`.trim()}
                        disabled={!checkTicketEnableEdit()}
                    />
                )}
                <Popover
                    id={`change-call-status-${
                        props.ticketDetail?.id || 'create_new_telephone_ticket_id'
                    }`}
                    key={`change-call-status-${
                        props.ticketDetail?.id || 'create_new_telephone_ticket_id'
                    }`}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    open={
                        isCallStatusListPopover &&
                        checkTicketEnableEdit() &&
                        Utils.isEmptyString(props.ticketDetail?.deletedAt) &&
                        !props.isDisableEdit
                    }
                    anchorEl={callStatusListPopover}
                    onClose={e => setCallStatusListPopover(null)}
                >
                    <div className="telephone-call-status-list">
                        {renderCallStatusItemList(
                            callStatusItemList,
                            setCurrentItemData,
                            setCallStatusListPopover,
                            currentItemData
                        )}
                    </div>
                </Popover>
            </>
        </div>
    );
};

export default TelephoneCallStatusFieldUI;

TelephoneCallStatusFieldUI.propTypes = {
    flagReset: PropTypes.any,
    currentItemData: PropTypes.any,
    setFlagReset: PropTypes.any,
    detailTicket: PropTypes.any,
    isDisableEdit: PropTypes.bool,
    isHelpDesk: PropTypes.bool,
    fieldData: PropTypes.object,
    API: PropTypes.any,
    currentPanelInitData: PropTypes.object,
    setIsEdited: PropTypes.func,
    setIsEditTicket: PropTypes.func,
    setShowButtonEditTicket: PropTypes.func,
    listFieldEdited: PropTypes.array,
    setListFieldEdited: PropTypes.func,
    setOnChangeEdit: PropTypes.func,
    onChangeEdit: PropTypes.bool,
    panelId: PropTypes.string,
    tabId: PropTypes.string,
    panelStatusKey: PropTypes.string,
    panelInitData: PropTypes.object,
    setPanelInitData: PropTypes.func,
    name: PropTypes.string,
    ticketDetail: PropTypes.object,
    defaultValue: PropTypes.string,
    data: PropTypes.object,
    key: PropTypes.string
};
